import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Box } from 'pcln-design-system'

const noop = () => {}

// Element that creates the shadow effect style if enabled, or render an empty div if not
const ShadowEffectElement = styled(Box)`
    z-index: 10;
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(51, 51, 51, 0.65);
    pointer-events: none;
`

class ShadowEffect extends React.PureComponent {
  static propTypes = {
    forProperty: PropTypes.string.isRequired,
    enabled: PropTypes.bool,
    alternativeBlurHandling: PropTypes.bool,
    children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)])
  }

  state = {
    isEnabled: false
  }

  constructor (props) {
    super(props)

    this.disableShadowState = (e) => this.setState((state, props) => ({ isEnabled: false }))
    this.enableShadowState = (e) => this.setState((state, props) => ({ isEnabled: true }))

    this.handleFocus = this.enableShadowState
    this.handleBlur = this.props.alternativeBlurHandling ? this.handleAlternativeBlur.bind(this) : this.disableShadowState
  }

  handleAlternativeBlur (e) {
    const target = e.nativeEvent.relatedTarget || e.nativeEvent.explicitOriginalTarget
    if (!target || !this.node.contains(target)) {
      this.setState((state, props) => ({ isEnabled: false }))
    }
  }

  handleDocumentClick = (e) => {
    if (e.target && this.node && !this.node.contains(e.target)) {
      this.disableShadowState()
    }
  }

  componentDidMount () {
    if (this.props.alternativeBlurHandling) {
      document.addEventListener('focusin', this.handleDocumentClick, true)
      document.addEventListener('click', this.handleDocumentClick, true)
    }
  }

  componentWillUnmount () {
    if (this.props.alternativeBlurHandling) {
      document.removeEventListener('focusin', this.handleAlternativeBlur)
      document.removeEventListener('click', this.handleAlternativeBlur)
    }
  }

  render () {
    const { forProperty, children, ...remainingProps } = this.props
    const { isEnabled } = this.state
    const { handleBlur, handleFocus, enableShadowState, disableShadowState } = this
    return (
      <Box width={1} onBlur={handleBlur} onFocus={handleFocus} {...remainingProps} >
        { isEnabled && (<ShadowEffectElement />) }
        <div ref={node => { this.node = node }} style={{ width: '100%', position: 'relative', zIndex: (isEnabled ? 20 : 'auto') }} >
          { typeof children === 'function' ? children({ isEnabled, enableShadowState, disableShadowState }) : (children) }
        </div>
      </Box>
    )
  }
}

ShadowEffect.displayName = 'ShadowEffect'

ShadowEffect.propTypes = {
  forProperty: PropTypes.string.isRequired,
  enabled: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
  onBlur: PropTypes.func,
  onFocus: PropTypes.func
}

const NoShadowEffect = ({ children }) => (
  <React.Fragment>
    { typeof children === 'function' ? children({ isEnabled: false, enableShadowState: noop, disableShadowState: noop }) : (children) }
  </React.Fragment>
)

NoShadowEffect.displayName = 'NoShadowEffect'

NoShadowEffect.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)])
}

const createShadowEffectWhen = (condition) => condition
  ? (props) => <ShadowEffect {...props} />
  : (props) => <NoShadowEffect {...props} />

export { ShadowEffect as default, NoShadowEffect, createShadowEffectWhen }
